/**
 * PhilosophicalGreeting - Random philosophical message on login
 * Displays a thought-provoking quote from a philosopher
 */

import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Quote, X } from "lucide-react";
import { Button } from "@/components/ui/button";

// Collection de messages philosophiques pertinents par thème du Bac
const PHILOSOPHICAL_GREETINGS = [
  // LA CONSCIENCE
  {
    philosopher: "Descartes",
    quote: "Je pense, donc je suis. Mais as-tu déjà pensé à ce que signifie vraiment 'penser' ?",
    theme: "La conscience",
    reflection: "La conscience de soi est-elle le fondement de toute certitude ?"
  },
  {
    philosopher: "Husserl",
    quote: "Toute conscience est conscience de quelque chose. Vers quoi ta conscience se tourne-t-elle aujourd'hui ?",
    theme: "La conscience",
    reflection: "L'intentionnalité définit-elle l'essence de la conscience ?"
  },
  {
    philosopher: "Freud",
    quote: "Le moi n'est pas maître dans sa propre maison. Connais-tu vraiment les forces qui te guident ?",
    theme: "L'inconscient",
    reflection: "La découverte de l'inconscient remet-elle en cause la liberté ?"
  },
  
  // LA LIBERTÉ
  {
    philosopher: "Sartre",
    quote: "L'homme est condamné à être libre. Qu'as-tu choisi de faire de ta liberté aujourd'hui ?",
    theme: "La liberté",
    reflection: "Sommes-nous responsables de tout ce que nous sommes ?"
  },
  {
    philosopher: "Spinoza",
    quote: "La liberté, c'est comprendre la nécessité. Es-tu libre quand tu comprends ce qui te détermine ?",
    theme: "La liberté",
    reflection: "La connaissance des causes nous libère-t-elle ?"
  },
  {
    philosopher: "Kant",
    quote: "Agis de telle sorte que tu traites l'humanité toujours comme une fin, jamais simplement comme un moyen.",
    theme: "Le devoir",
    reflection: "Le respect de la dignité humaine est-il le fondement de la morale ?"
  },
  
  // LA VÉRITÉ
  {
    philosopher: "Platon",
    quote: "La vérité est comme le soleil : on ne peut la regarder en face, mais sans elle, on ne voit rien.",
    theme: "La vérité",
    reflection: "Peut-on accéder à la vérité par la seule raison ?"
  },
  {
    philosopher: "Nietzsche",
    quote: "Il n'y a pas de faits, seulement des interprétations. Quelle est ton interprétation du monde ?",
    theme: "La vérité",
    reflection: "La vérité est-elle une construction humaine ?"
  },
  {
    philosopher: "Socrate",
    quote: "Je sais que je ne sais rien. C'est le début de la sagesse. Es-tu prêt à questionner tes certitudes ?",
    theme: "La vérité",
    reflection: "Le doute est-il le chemin vers la connaissance ?"
  },
  
  // LE BONHEUR
  {
    philosopher: "Aristote",
    quote: "Le bonheur est l'activité de l'âme conforme à la vertu. Quelle vertu exerceras-tu aujourd'hui ?",
    theme: "Le bonheur",
    reflection: "Le bonheur réside-t-il dans l'action vertueuse ?"
  },
  {
    philosopher: "Épicure",
    quote: "Le plaisir est le commencement et la fin de la vie heureuse. Mais sais-tu distinguer les vrais plaisirs ?",
    theme: "Le bonheur",
    reflection: "Tous les plaisirs se valent-ils ?"
  },
  {
    philosopher: "Pascal",
    quote: "Tout le malheur des hommes vient d'une seule chose : ne pas savoir demeurer en repos dans une chambre.",
    theme: "Le bonheur",
    reflection: "Le divertissement nous éloigne-t-il de nous-mêmes ?"
  },
  
  // L'ART
  {
    philosopher: "Hegel",
    quote: "L'art est la manifestation sensible de l'Idée. Quelle vérité l'art te révèle-t-il ?",
    theme: "L'art",
    reflection: "L'art dit-il quelque chose que la philosophie ne peut pas dire ?"
  },
  {
    philosopher: "Kant",
    quote: "Le beau est ce qui plaît universellement sans concept. As-tu déjà ressenti cette beauté pure ?",
    theme: "L'art",
    reflection: "Le jugement esthétique est-il subjectif ou universel ?"
  },
  
  // LA JUSTICE
  {
    philosopher: "Platon",
    quote: "La justice, c'est quand chacun fait ce qu'il doit faire. Mais qui décide de ce que chacun doit faire ?",
    theme: "La justice",
    reflection: "La justice est-elle naturelle ou conventionnelle ?"
  },
  {
    philosopher: "Rawls",
    quote: "Imagine que tu ne sais pas quelle place tu occuperas dans la société. Quelle justice choisirais-tu ?",
    theme: "La justice",
    reflection: "Le voile d'ignorance permet-il de penser une société juste ?"
  },
  
  // L'ÉTAT
  {
    philosopher: "Hobbes",
    quote: "L'homme est un loup pour l'homme. L'État est-il le remède ou le symptôme ?",
    theme: "L'État",
    reflection: "L'État est-il nécessaire pour sortir de l'état de nature ?"
  },
  {
    philosopher: "Rousseau",
    quote: "L'homme est né libre, et partout il est dans les fers. Comment retrouver cette liberté originelle ?",
    theme: "L'État",
    reflection: "Le contrat social nous libère-t-il ou nous aliène-t-il ?"
  },
  
  // LE TRAVAIL
  {
    philosopher: "Marx",
    quote: "Le travail devrait être l'expression de notre humanité, non son aliénation. Ton travail t'épanouit-il ?",
    theme: "Le travail",
    reflection: "Le travail peut-il être une source de réalisation de soi ?"
  },
  {
    philosopher: "Arendt",
    quote: "Le travail assure la survie, l'œuvre crée un monde, l'action révèle qui nous sommes.",
    theme: "Le travail",
    reflection: "Quelle différence entre travailler, œuvrer et agir ?"
  },
  
  // LA TECHNIQUE
  {
    philosopher: "Heidegger",
    quote: "La technique n'est pas neutre. Elle transforme notre rapport au monde. En es-tu conscient ?",
    theme: "La technique",
    reflection: "La technique nous libère-t-elle ou nous asservit-elle ?"
  },
  
  // LE TEMPS
  {
    philosopher: "Saint Augustin",
    quote: "Qu'est-ce que le temps ? Si personne ne me le demande, je le sais ; si je veux l'expliquer, je ne le sais plus.",
    theme: "Le temps",
    reflection: "Le temps existe-t-il indépendamment de la conscience ?"
  },
  {
    philosopher: "Bergson",
    quote: "Le temps vécu n'est pas le temps mesuré. Sais-tu écouter ta durée intérieure ?",
    theme: "Le temps",
    reflection: "La science peut-elle saisir le temps tel que nous le vivons ?"
  },
  
  // LA RELIGION
  {
    philosopher: "Pascal",
    quote: "Le cœur a ses raisons que la raison ne connaît point. La foi dépasse-t-elle la raison ?",
    theme: "La religion",
    reflection: "Foi et raison sont-elles compatibles ?"
  },
  {
    philosopher: "Saint Thomas d'Aquin",
    quote: "La grâce ne détruit pas la nature, mais la parfait. Comment la foi éclaire-t-elle la raison ?",
    theme: "La religion",
    reflection: "La philosophie peut-elle conduire à Dieu ?"
  },
  
  // LA NATURE
  {
    philosopher: "Rousseau",
    quote: "Tout est bien sortant des mains de l'Auteur des choses, tout dégénère entre les mains de l'homme.",
    theme: "La nature",
    reflection: "La civilisation corrompt-elle notre nature ?"
  },
  
  // AUTRUI
  {
    philosopher: "Levinas",
    quote: "Le visage d'autrui m'ordonne : 'Tu ne tueras point'. As-tu regardé vraiment le visage de l'autre ?",
    theme: "Autrui",
    reflection: "La rencontre d'autrui est-elle le fondement de l'éthique ?"
  },
  {
    philosopher: "Sartre",
    quote: "L'enfer, c'est les autres. Mais sans les autres, qui serais-tu ?",
    theme: "Autrui",
    reflection: "Autrui est-il nécessaire à la conscience de soi ?"
  }
];

const PhilosophicalGreeting = ({ studentName, onDismiss }) => {
  const [greeting, setGreeting] = useState(null);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Select a random greeting
    const randomIndex = Math.floor(Math.random() * PHILOSOPHICAL_GREETINGS.length);
    setGreeting(PHILOSOPHICAL_GREETINGS[randomIndex]);
    
    // Auto-dismiss after 15 seconds
    const timer = setTimeout(() => {
      handleDismiss();
    }, 15000);
    
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    onDismiss?.();
  };

  if (!visible || !greeting) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 animate-in fade-in duration-300" data-testid="philosophical-greeting">
      <Card className="max-w-lg w-full bg-gradient-to-br from-[#1A365D] to-[#2D4A6F] text-white shadow-2xl border-0 animate-in zoom-in duration-300">
        <CardContent className="p-6 relative">
          {/* Close button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-white/60 hover:text-white hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </Button>

          {/* Quote icon */}
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <Quote className="w-6 h-6 text-[#D4AF37]" />
            </div>
          </div>

          {/* Greeting */}
          <p className="text-white/80 text-sm text-center mb-2">
            Bienvenue, {studentName} !
          </p>

          {/* Quote */}
          <blockquote className="text-lg md:text-xl text-center font-medium mb-4 leading-relaxed" style={{ fontFamily: 'Playfair Display, serif' }}>
            "{greeting.quote}"
          </blockquote>

          {/* Philosopher */}
          <p className="text-[#D4AF37] text-center font-medium mb-4">
            — {greeting.philosopher}
          </p>

          {/* Theme badge */}
          <div className="flex justify-center mb-4">
            <span className="px-3 py-1 bg-white/10 rounded-full text-xs text-white/80">
              📚 {greeting.theme}
            </span>
          </div>

          {/* Reflection question */}
          <div className="bg-white/10 rounded-lg p-3 mb-4">
            <p className="text-sm text-white/90 text-center italic">
              💭 {greeting.reflection}
            </p>
          </div>

          {/* CTA */}
          <Button
            onClick={handleDismiss}
            className="w-full bg-[#D4AF37] hover:bg-[#B3932E] text-[#0F172A] font-medium"
          >
            Commencer à philosopher
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PhilosophicalGreeting;
